import type { z } from "zod/v4";
import { BasePathParamsSchema, TenantPathParams } from "../index";
import type { ResourceConfig, ResourceParams, ResourceScope } from "../resource";
import type { ScopedPathParams } from "../endpoint";

/**
 * Wire format for partial path params used in invalidation.
 */
interface PartialPathWire {
  tenantId?: string;
  siteId?: string;
  assetCameraKey?: string;
  [key: string]: string | undefined;
}

/**
 * Shape of a query key in wire format.
 */
interface QueryKeyShape {
  feature?: string;
  resource?: string;
  scope?: ResourceScope;
  path?: PartialPathWire;
}

/**
 * Type guard to check if a value looks like a query key.
 */
function isQueryKeyShape(value: unknown): value is QueryKeyShape {
  return typeof value === "object" && value !== null;
}

/**
 * Extracts extra path params (non-base params) from a partial path object.
 */
function extractExtraPathParams(params: Record<string, unknown>): PartialPathWire {
  const result: PartialPathWire = {};
  for (const [k, v] of Object.entries(params)) {
    if (!["tenantId", "siteId", "assetCameraKey"].includes(k) && typeof v === "string") {
      result[k] = v;
    }
  }
  return result;
}

/**
 * Creates an invalidation predicate for React Query.
 *
 * Takes a resource and optional path params. Invalidates queries that:
 * 1. Match the specified resource (feature + resource path + scope)
 * 2. Would include the specified path params (exact match OR broader/wildcard queries)
 *
 * Example: Invalidating snapshots for a specific camera will also invalidate:
 * - Queries for all snapshots at that site (less specific)
 * - Queries for all snapshots for that tenant (even less specific)
 *
 * This ensures that when data changes for a specific camera, all queries that
 * might include that camera's data are invalidated.
 *
 * Note: This operates on resources, not endpoints, because invalidation should
 * affect all endpoints that share the same resource path, regardless of which
 * endpoint created the cached query or what query params were used.
 */
export function invalidateQueriesFor<
  TResource extends string,
  TResourcePathSchema extends z.ZodType<unknown, ResourceParams<TResource>>,
  TScope extends ResourceScope,
>(
  resource: ResourceConfig<TResource, TResourcePathSchema, TScope>,
  params?: {
    path?: Partial<ScopedPathParams<TScope> & z.output<TResourcePathSchema>>;
  },
) {
  const scope = resource.scope ?? "camera";

  // Encode base path params to wire format using the appropriate schema based on scope
  const pathWire =
    params?.path ?
      scope === "tenant" ?
        TenantPathParams.partial().encode(params.path)
      : BasePathParamsSchema.partial().encode(params.path)
    : undefined;

  // Encode extra resource path params - these are already strings in the schema
  const extraPathWire = params?.path && resource.resourcePathSchema ? extractExtraPathParams(params.path) : undefined;

  return {
    predicate: (query: { queryKey: readonly unknown[] }) => {
      const keyValue = query.queryKey[0];
      if (!isQueryKeyShape(keyValue)) return false;

      const key = keyValue;
      if (!key.feature) return false;

      // Must match resource and scope
      if (key.feature !== resource.feature) return false;
      if (key.resource !== resource.resource) return false;
      if (key.scope !== scope) return false;

      // If no params specified, invalidate all queries for this resource
      if (!pathWire) return true;

      const keyPath = key.path;
      if (!keyPath) return false;

      // Must match tenant if specified
      if (pathWire.tenantId && keyPath.tenantId !== pathWire.tenantId) return false;

      // For camera-scoped resources, also check site and camera
      if (scope === "camera") {
        const cameraPathWire = pathWire as { siteId?: string; assetCameraKey?: string };
        // For site and camera, we invalidate if:
        // 1. The query is broader (wildcard/undefined) - it includes our specific data
        // 2. The query matches exactly
        if (cameraPathWire.siteId) {
          // Invalidate if query has no siteId (broader) or matches exactly
          if (keyPath.siteId && keyPath.siteId !== cameraPathWire.siteId) return false;
        }

        if (cameraPathWire.assetCameraKey) {
          // Invalidate if query has no assetCameraKey (broader) or matches exactly
          if (keyPath.assetCameraKey && keyPath.assetCameraKey !== cameraPathWire.assetCameraKey) return false;
        }
      }

      // Check extra path params (e.g., snapshotId) - must match exactly if specified
      if (extraPathWire) {
        for (const [k, v] of Object.entries(extraPathWire)) {
          if (keyPath[k] !== v) return false;
        }
      }

      return true;
    },
  };
}
